/**
 * JobDescription — RC 1.25.F.1 format-aware description renderer.
 *
 * Ingested job descriptions arrive in several real-world shapes:
 *
 *   1. Plain text with newlines (Lever `descriptionPlain`, most
 *      user_import jobs).
 *   2. Valid HTML (`<p>…</p><ul><li>…</li></ul>`) — for rows stored
 *      before the RC 1.25.F.1 parse-time fix, and for user_import
 *      rows whose source HTML was ingested unchanged.
 *   3. Entity-escaped HTML (the Stripe / Greenhouse regression root
 *      cause: `&lt;h2&gt;Who we are&lt;/h2&gt;` stored as "text").
 *   4. Mixed / malformed fragments with stray tags, inline styles,
 *      unclosed anchors, embedded `<span style="…">` clutter, or
 *      mid-sentence HTML entities (`&nbsp;`, `&amp;`, `&#039;`).
 *
 * Rules:
 *   - Never use dangerouslySetInnerHTML. Every rendered element is a
 *     real React element created from a whitelist of tags.
 *   - If input looks like HTML (raw OR entity-escaped), parse with
 *     DOMParser and walk the resulting tree.
 *   - If input looks like plain text, render paragraphs split on
 *     blank lines; preserve single newlines as soft breaks.
 *   - Unknown tags contribute their text content only; unknown
 *     attributes are dropped.
 *   - Anchors keep `href` only (normalised to `https?://` or relative
 *     `/path`), force `rel="noopener noreferrer"` + `target="_blank"`.
 *   - Fallback if anything unexpected happens: show the raw text
 *     verbatim in a pre-wrap block rather than crashing the page.
 *
 * Deliberately tiny — no npm sanitizer dependency. For JD-scale
 * content the browser DOMParser + a tag whitelist is more than
 * enough and avoids a 40KB bundle addition.
 */

import React, { useMemo } from 'react';

// Whitelisted tags we know how to render. Anything else falls through
// to its children as plain text.
const BLOCK_TAGS = new Set([
  'p', 'div', 'section', 'article',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote',
  'br', 'hr',
]);
const INLINE_TAGS = new Set(['strong', 'b', 'em', 'i', 'u', 'a', 'span', 'code']);

// Quick sniff: does the text contain at least one plausibly-HTML tag?
// Only checks for a small whitelist of common JD tags to avoid false
// positives on plain prose that mentions "<" (e.g. "the role is <$200k").
const HTML_TAG_RE = /<(\/?\s*)(p|br|h[1-6]|ul|ol|li|div|strong|b|em|i|a|span|hr|blockquote|section|article)\b/i;
const ENTITY_HTML_RE = /&lt;\s*\/?\s*(p|br|h[1-6]|ul|ol|li|div|strong|b|em|i|a|span)\b/i;

function classify(input) {
  const s = (input || '').trim();
  if (!s) return 'empty';
  if (ENTITY_HTML_RE.test(s)) return 'escaped_html';
  if (HTML_TAG_RE.test(s))    return 'html';
  return 'text';
}

// Minimal entity decode — just enough to catch the escaped-HTML case.
// Uses a throwaway textarea so the browser handles every named entity
// (&nbsp;, &#x27;, …) without us shipping a lookup table.
function decodeEntities(s) {
  if (typeof document === 'undefined') return s;
  const ta = document.createElement('textarea');
  ta.innerHTML = s;
  return ta.value;
}

function safeHref(raw) {
  if (!raw) return null;
  try {
    const u = String(raw).trim();
    if (!u) return null;
    // Allow absolute http(s) and simple relative paths. Strip `javascript:`
    // or other schemes defensively.
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith('/') || u.startsWith('#')) return u;
    return null;
  } catch { return null; }
}

// Recursive node → React renderer. Returns null for nodes we choose
// to drop entirely (e.g. <script>, <style>, unknown rubbish with no
// text children).
function renderNode(node, key) {
  // Text node.
  if (node.nodeType === 3) {
    const t = node.nodeValue;
    if (!t) return null;
    return t;
  }
  if (node.nodeType !== 1) return null; // comments, PIs, etc.
  const tag = (node.tagName || '').toLowerCase();

  // Drop noise outright.
  if (tag === 'script' || tag === 'style' || tag === 'noscript' ||
      tag === 'iframe' || tag === 'object' || tag === 'embed') {
    return null;
  }

  // Render children first — we reuse this for both known and unknown
  // containers.
  const kids = [];
  node.childNodes.forEach((child, i) => {
    const r = renderNode(child, `${key}-${i}`);
    if (r !== null && r !== undefined && r !== '') kids.push(r);
  });
  const hasKids = kids.length > 0;

  if (tag === 'br') {
    return <br key={key} />;
  }
  if (tag === 'hr') {
    return <hr key={key} className="my-4 border-ndor-edge" />;
  }
  if (tag === 'a') {
    const href = safeHref(node.getAttribute('href'));
    if (!href) return hasKids ? <span key={key}>{kids}</span> : null;
    return (
      <a
        key={key}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-ndor-primary underline underline-offset-2 hover:text-ndor-primary-hover"
      >
        {hasKids ? kids : href}
      </a>
    );
  }
  if (!hasKids) return null;

  if (tag === 'h1' || tag === 'h2') {
    return <h3 key={key} className="text-base font-semibold text-ndor-ink mt-4 mb-1">{kids}</h3>;
  }
  if (tag === 'h3' || tag === 'h4') {
    return <h4 key={key} className="text-sm font-semibold text-ndor-ink mt-3 mb-1">{kids}</h4>;
  }
  if (tag === 'h5' || tag === 'h6') {
    return <h5 key={key} className="text-sm font-medium text-ndor-ink mt-2 mb-1">{kids}</h5>;
  }
  if (tag === 'p' || tag === 'div' || tag === 'section' || tag === 'article') {
    return <p key={key} className="mb-2 leading-relaxed">{kids}</p>;
  }
  if (tag === 'ul') {
    return <ul key={key} className="list-disc pl-5 mb-2 space-y-1">{kids}</ul>;
  }
  if (tag === 'ol') {
    return <ol key={key} className="list-decimal pl-5 mb-2 space-y-1">{kids}</ol>;
  }
  if (tag === 'li') {
    return <li key={key} className="leading-relaxed">{kids}</li>;
  }
  if (tag === 'blockquote') {
    return <blockquote key={key} className="border-l-2 border-ndor-edge pl-3 italic text-ndor-ink-muted mb-2">{kids}</blockquote>;
  }
  if (tag === 'strong' || tag === 'b') {
    return <strong key={key} className="font-semibold text-ndor-ink">{kids}</strong>;
  }
  if (tag === 'em' || tag === 'i') {
    return <em key={key}>{kids}</em>;
  }
  if (tag === 'u') {
    return <u key={key}>{kids}</u>;
  }
  if (tag === 'code') {
    return <code key={key} className="font-mono text-[0.85em] bg-ndor-surface-alt px-1 rounded">{kids}</code>;
  }
  if (INLINE_TAGS.has(tag) || BLOCK_TAGS.has(tag)) {
    return <span key={key}>{kids}</span>;
  }
  // Unknown tag → flatten to its text children so nothing gets dropped
  // wholesale, but the unknown wrapper vanishes.
  return <React.Fragment key={key}>{kids}</React.Fragment>;
}

function renderHtml(html) {
  try {
    const parser = new DOMParser();
    // Wrap so parseFromString always gets a <body>.
    const doc = parser.parseFromString(`<!DOCTYPE html><body>${html}</body>`, 'text/html');
    const body = doc.body;
    if (!body) return null;
    const out = [];
    body.childNodes.forEach((n, i) => {
      const r = renderNode(n, `root-${i}`);
      if (r !== null && r !== undefined && r !== '') out.push(r);
    });
    return out.length ? out : null;
  } catch (_) {
    return null;
  }
}

function renderPlainText(text) {
  // Paragraphs split on blank lines; preserve single newlines as
  // soft breaks inside a paragraph. Collapse runs of whitespace.
  const paras = text
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map(p => p.trim())
    .filter(Boolean);
  if (paras.length === 0) return null;
  return paras.map((p, i) => (
    <p key={`pp-${i}`} className="mb-2 leading-relaxed whitespace-pre-wrap">
      {p}
    </p>
  ));
}

export default function JobDescription({ text, className = '', testid = 'job-detail-description' }) {
  const rendered = useMemo(() => {
    const kind = classify(text);
    if (kind === 'empty') return null;

    // Path 1: entity-escaped HTML (the bug we're here to fix) — decode
    // entities first, then fall through to the html path.
    let source = text;
    if (kind === 'escaped_html') {
      source = decodeEntities(text);
    }

    // Path 2: HTML.
    if (kind === 'html' || kind === 'escaped_html') {
      const nodes = renderHtml(source);
      if (nodes && nodes.length > 0) return nodes;
      // Fell through — DOMParser produced nothing useful. Graceful
      // degradation: render text content as plain paragraphs.
      const fallback = (source || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      return renderPlainText(fallback);
    }

    // Path 3: plain text.
    return renderPlainText(source);
  }, [text]);

  if (!rendered) return null;
  return (
    <div
      className={`text-sm text-ndor-ink-secondary leading-relaxed font-manrope ${className}`}
      data-testid={testid}
    >
      {rendered}
    </div>
  );
}
